/*******************************************************
 * Copyright (C) BoostCommerce.net
 *
 * This file is part of commercial BoostCommerce.net projects.
 *
 * This file can not be copied and/or distributed without the express
 * permission of BoostCommerce.net
 *
 * @Date:   2022-12-05 13:33:30
 *
 *******************************************************/
// @flow
import _ from 'lodash';
import React, { Component } from 'react';
import { Icon, ChoiceList, TextField, Popover, Button, Stack, Tag } from '@shopify/polaris';
import { SearchMinor } from '@shopify/polaris-icons';
import { withI18n } from '@shopify/react-i18n';
import RenderDatePicker from './render-date-picker';
import { STATUS_CHOICES } from 'src/constants/merchandising-v2';
import { displayDateRange } from 'src/helpers/date-time-helper';

import type { ComposedProps, State } from './index.type';

class FiltersBar extends Component<ComposedProps, State> {
  state = {
    activePopoverFilters: false,
    activePopoverDate: false,
    formattedStatusChoices: [],
    selectedDate: {},
    currentStatus: null
  };

  componentDidMount() {
    const { i18n, status, enabledFromDate, enabledToDate } = this.props;
    const formattedStatusChoices = STATUS_CHOICES.map(function (choice) {
      return {
        ...choice,
        label: i18n.translate(choice.label)
      };
    });

    this.setState({
      formattedStatusChoices,
      currentStatus: status,
      selectedDate: {
        start: enabledFromDate,
        end: enabledToDate
      }
    });
  }

  // Render apply filter for filter
  renderApplyFilter = () => {
    const { status, enabledFromDate, enabledToDate, handleStatusChange, onChooseDate } = this.props;
    const appliedFilters = [];
    if (!_.isEmpty(status)) {
      const key = 'status';
      appliedFilters.push({
        key,
        label: this.disambiguateLabel(key),
        onRemove: () => {
          handleStatusChange([]);
          this.handleUpdateStatus([]);
        }
      });
    }
    if (!!enabledFromDate || !!enabledToDate) {
      const key = 'date';
      appliedFilters.push({
        key,
        label: this.disambiguateLabel(key),
        onRemove: () => {
          onChooseDate({ start: '', end: '' });
          this.handleUpdateChooseDate({ start: '', end: '' });
        }
      });
    }

    const tagMarkUp = _.map(appliedFilters, appliedFilter => {
      const { key, label, onRemove } = appliedFilter;
      return (
        <Tag key={key} onRemove={onRemove}>
          {label}
        </Tag>
      );
    });

    return <Stack spacing="tight">{tagMarkUp}</Stack>;
  };

  render() {
    const { i18n, queryValue, onQueryChange, handleKeyUp, handleKeyDown, appLanguage, panelID } =
      this.props;

    const {
      activePopoverFilters,
      activePopoverDate,
      formattedStatusChoices,
      currentStatus,
      selectedDate
    } = this.state;

    const activatorFilters = (
      <Button onClick={() => this.handleTogglePopover('activePopoverFilters')} disclosure>
        {i18n.translate('AdminHTML.MerchandisingV2.FiltersBar.filters')}
      </Button>
    );

    return (
      <div className="BC-Filters">
        <div className="BC-Filters-ConnectedFilterControl">
          <div className="BC-Filters-ConnectedFilterControl__CenterContainer">
            <Popover
              active={activePopoverFilters}
              activator={activatorFilters}
              onClose={() =>
                !activePopoverDate && this.handleTogglePopover('activePopoverFilters')
              }>
              <Popover.Pane>
                <Popover.Section>
                  <div className="BC-Popover__MerchandisingV2-Filters-Options">
                    <Stack vertical>
                      <ChoiceList
                        title={i18n.translate(
                          'AdminHTML.MerchandisingV2.FiltersBar.statusFilter_Label'
                        )}
                        choices={formattedStatusChoices}
                        selected={currentStatus}
                        onChange={this.handleUpdateStatus}
                      />
                      <RenderDatePicker
                        onChooseDate={this.handleUpdateChooseDate}
                        enabledFromDate={selectedDate.start}
                        stylePlaceholder="BC-Page__Merchandising-FilterBar-ActiveDate"
                        placeholder={i18n.translate(
                          'AdminHTML.MerchandisingV2.FiltersBar.dateFilter_Placeholder'
                        )}
                        label={i18n.translate(
                          'AdminHTML.MerchandisingV2.FiltersBar.dateFilter_Label'
                        )}
                        enabledToDate={selectedDate.end}
                        appLanguage={appLanguage}
                        popoverActive={activePopoverDate}
                        togglePopoverDateActive={() =>
                          this.handleTogglePopover('activePopoverDate')
                        }
                      />
                      <div className="breakLine">
                        <Button onClick={this.handleClearAll} plain>
                          {i18n.translate('AdminHTML.Common.Action.clearAll')}
                        </Button>
                        <Button onClick={this.handleApplyFilter} primary>
                          {i18n.translate('AdminHTML.Common.Action.apply')}
                        </Button>
                      </div>
                    </Stack>
                  </div>
                </Popover.Section>
              </Popover.Pane>
            </Popover>
          </div>
          <div className="BC-Filters-ConnectedFilterControl__RightContainer">
            <div className="search" onKeyDown={handleKeyDown} onKeyUp={handleKeyUp}>
              <TextField
                onChange={onQueryChange}
                value={queryValue}
                prefix={<Icon source={SearchMinor} color="base" />}
                placeholder={i18n.translate(
                  `AdminHTML.MerchandisingV2.FiltersBar.${
                    panelID === 'search'
                      ? 'textFieldCommerce_Placeholder'
                      : 'textFieldCollection_Placeholder'
                  }`
                )}
              />
            </div>
          </div>
        </div>
        <div className="BC-Filters__TagsContainer">{this.renderApplyFilter()}</div>
      </div>
    );
  }

  // Handle toggle popover active
  handleTogglePopover = (fieldName: string) => {
    const value = this.state[fieldName];
    this.setState({ [fieldName]: !value });

    if (fieldName === 'activePopoverDate') {
      // Keep Filter popover always open
      this.setState({ activePopoverFilters: true });
    }
  };

  handleUpdateChooseDate = selectedDate => {
    this.setState({ selectedDate });
  };

  handleUpdateStatus = value => {
    this.setState({ currentStatus: value });
  };

  // Handle apply filter
  handleApplyFilter = () => {
    const { selectedDate, currentStatus } = this.state;
    this.props.handleApplyFilterRule(selectedDate, currentStatus);
    this.handleTogglePopover('activePopoverFilters');
  };

  // Handle clear all filter
  handleClearAll = () => {
    this.setState({
      currentStatus: [],
      selectedDate: {
        start: '',
        end: ''
      }
    });
  };

  disambiguateLabel = type => {
    const { i18n } = this.props;
    switch (type) {
      case 'status': {
        const { status } = this.props;
        const { formattedStatusChoices } = this.state;

        const statusObject = _.find(formattedStatusChoices, item => item.value === status[0]);
        const statusLabel = statusObject ? statusObject.label : '';

        return i18n.translate('AdminHTML.MerchandisingV2.FiltersBar.statusDisplay', {
          nt_status: statusLabel
        });
      }
      case 'date': {
        const { enabledFromDate, enabledToDate } = this.props;
        const dateLabel = displayDateRange(enabledFromDate, enabledToDate);

        return i18n.translate('AdminHTML.MerchandisingV2.FiltersBar.dateDisplay', {
          nt_date: dateLabel
        });
      }
      default: {
        return;
      }
    }
  };
}

export default withI18n()(FiltersBar);
